import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { PoundSterling } from "lucide-react";
import { API } from "../config/api";
import { getUserRole, ROLES } from "../config/roleConfig";

const STAGE_SHORT = {
  invoice_issued: { label: "INV", color: "text-teal" },
  reminder_1: { label: "R1", color: "text-yellow-400" },
  call_1: { label: "C1", color: "text-yellow-400" },
  reminder_2: { label: "R2", color: "text-status-amber" },
  call_2: { label: "C2", color: "text-status-amber" },
  reminder_3: { label: "R3", color: "text-status-red" },
  call_3: { label: "C3", color: "text-status-red" },
  lba_sent: { label: "LBA", color: "text-gray-300" },
  proceedings: { label: "PROC", color: "text-gray-300" },
};

function resolveEmail() {
  // Prefer sessionStorage bonsai_user_email — most reliable post-login.
  // Fall back through localStorage variants before touching the user object.
  return (
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    (() => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        return user.user_email || user.email || user.ms_email || "";
      } catch { return ""; }
    })()
  ).toLowerCase().trim();
}

function headers() {
  const email = resolveEmail();
  return {
    "X-Bonsai-User-Id": localStorage.getItem("bonsai_user_id") || sessionStorage.getItem("bonsai_user_id") || "",
    "X-User-Email": email,
  };
}

export default function CreditControlWidget() {
  // ── Internal role guard ─────────────────────────────────────────────
  // CreditControlWidget is for May (ACCOUNTS role) only.
  // Guard here so it cannot bleed to other roles even if Dashboard.jsx
  // has a stale isAccountsRole value due to email resolution issues.
  const email = resolveEmail();
  const role = getUserRole(email);
  if (role !== ROLES.ACCOUNTS) return null;
  // ───────────────────────────────────────────────────────────────────

  return <CreditControlWidgetInner />;
}

function CreditControlWidgetInner() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/credit-control/list`, {
          params: { status: "active,part_paid" },
          headers: headers(),
        });
        setRecords(res.data.records || []);
      } catch {
        // silently fail — widget is optional
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || records.length === 0) return null;

  const total = records.reduce((s, r) => s + (r.total_outstanding || 0), 0);

  return (
    <div className="bg-navy-card border border-white/10 rounded-xl p-4" data-testid="cc-widget">
      <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-3">
        <PoundSterling className="w-4 h-4 text-teal" />
        Outstanding Invoices — My Matters
      </h3>
      <div className="space-y-1.5">
        {records.slice(0, 5).map(r => {
          const stg = STAGE_SHORT[r.escalation_stage] || { label: "?", color: "text-gray-400" };
          return (
            <div
              key={r.id}
              className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 cursor-pointer hover:bg-white/10 transition-colors"
              onClick={() => navigate(`/credit-control/${r.id}`)}
              data-testid={`cc-widget-row-${r.id}`}
            >
              <div className="flex-1 min-w-0">
                <span className="text-xs font-mono text-gray-400 mr-2">{r.matter_ref}</span>
                <span className="text-sm text-white truncate">{r.client_name}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-bold text-white">
                  £{(r.total_outstanding || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                </span>
                <span className={`text-xs font-bold ${stg.color}`}>{stg.label}</span>
              </div>
            </div>
          );
        })}
      </div>
      {records.length > 5 && (
        <p className="text-xs text-gray-500 mt-2 text-center">+ {records.length - 5} more</p>
      )}
      <div className="border-t border-white/10 mt-3 pt-2 flex justify-between text-sm">
        <span className="text-gray-400">Total outstanding (my matters)</span>
        <span className="font-bold text-teal">
          £{total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}
        </span>
      </div>
    </div>
  );
}
